import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faEye, faHome, faPlus, faLayerGroup, faSearch, faBook, faTrash, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { confirmDeleteToast, sendError, sendSuccess } from "../helpers/Helper";
import { API_URL_ENDPOINT } from "../appsetting";
import { useNavigate } from "react-router-dom";
import { Routess } from "../Routes/routes";
import { useApi } from "../helpers/useApi";
import { Model } from "../components/Model";
import { Textbox } from "../components/Textbox";
import { theme } from "../constants/theme";

export const BooksCategory = () => {
  const callApi = useApi();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [showModel, setShowAddModel] = useState(false);
  const [data, setData] = useState({
    items: [],
    pageNo: 1,
    pageSize: 12,
    totalPage: 0,
    totalRecords: 0,
  });

  // Main Form
  const bookForm = useForm({
    defaultValues: {
      id: "0",
      title: "",
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    watch,
  } = bookForm;

  useEffect(() => {
    getData(1);
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => {
      if (isTyping) {
        setIsTyping(false);
        getData(1);
      }
    }, 300);

    return () => clearTimeout(delay);
  }, [searchTerm]);

  const getData = async (cp = null, append = false) => {
    try {
      setDataLoading(true);
      const filters = [];
      if (searchTerm) filters.push(`SearchText:${searchTerm}`);

      const queryParams = [
        filters.length ? `filters=${filters.join(";")}` : null,
        `pageNo=${cp ?? data.pageNo}`,
        `pageSize=${data.pageSize}`,
        `sortOrder=desc`
      ].filter(Boolean).join("&");

      const response = await callApi(API_URL_ENDPOINT.BooksCategory + `?${queryParams}`);
      if (response.status) {
        if (append) {
          setData((prev) => ({
            ...response.data,
            items: [...prev.items, ...response.data.items],
          }));
        } else {
          setData(response.data);
        }
      }
    } catch {
      setData((prev) => ({ ...prev, items: [] }));
    } finally {
      setDataLoading(false);
    }
  };

  const handleView = (id) => {
    navigate(Routess.BooksCategory.path + `/${id}`);
  };

  const handleDelete = () => {
    confirmDeleteToast(() => {
      sendSuccess("Deleted successfully!");
    });
  };

  const addBookCategory = async (formValues) => {
    try {
      const payload = {
        title: formValues.title,
      };

      const response = await callApi(API_URL_ENDPOINT.Books + '/category', {
        method: "POST",
        data: payload,
      });

      if (response.status) {
        sendSuccess(response.message);
        setShowAddModel(false);
        getData(1);
        reset();
      } else {
        throw new Error(response.message);
      }
    } catch (ex) {
      sendError(ex.message);
    }
  };

  return (
    <div className="w-full">
      {/* 🔹 Add Category Modal */}
      <Model show={showModel} size="md">
        {/* Modal Header */}
        <div className="flex justify-between items-center px-4 py-3 bg-gradient-to-r from-slate-800 to-slate-900 border-b border-accent/20 text-white rounded-t-2xl">
          <div className="flex items-center">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary to-orange-600 flex items-center justify-center mr-3 shadow-lg shadow-primary/30">
              <FontAwesomeIcon icon={faLayerGroup} className="text-white text-sm" />
            </div>
            <h5 className="font-bold text-base m-0">Add Category</h5>
          </div>
          <button
            onClick={() => setShowAddModel(false)}
            className="w-8 h-8 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-all text-white"
          >
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 bg-light">
          <FormProvider {...bookForm}>
            <form onSubmit={handleSubmit(addBookCategory)}>
              <div className="mb-6">
                <h6 className="mb-2 text-sm font-bold text-dark">🏷️ Category Details</h6>
                <Textbox
                  Label="Category Title"
                  name="title"
                  Placeholder="Enter category name"
                  validationRules={{ required: "Title is required" }}
                  register={register}
                  errors={errors}
                  watch={watch}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-lightGray">
                <button
                  type="button"
                  onClick={() => setShowAddModel(false)}
                  className="px-4 py-2 rounded-lg bg-white border border-lightGray text-gray font-semibold text-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-lg bg-gradient-to-br from-primary to-orange-600 text-white font-semibold text-sm shadow-md hover:shadow-lg disabled:opacity-70 transition-all"
                >
                  {isSubmitting ? 'Saving...' : 'Save Category'}
                </button>
              </div>
            </form>
          </FormProvider>
        </div>
      </Model>

      {/* Page Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded-xl shadow-sm border border-lightGray">
        <div>
          <div className="flex items-center mt-0">
            <div>
              <h4 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-br from-primary to-accent mb-0">
                Categories
              </h4>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative w-full md:w-auto">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input
              type="text"
              placeholder="Search categories..."
              className="pl-10 pr-4 py-2 rounded-lg border border-lightGray bg-white focus:ring-2 focus:ring-accent/20 focus:border-accent outline-none w-full md:w-56 transition-all"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setIsTyping(true);
              }}
            />
          </div>
          <button
            onClick={() => setShowAddModel(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-orange-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all whitespace-nowrap"
          >
            <FontAwesomeIcon icon={faPlus} /> New Category
          </button>
        </div>
      </div>

      {/* Categories Grid Container */}
      <div className="bg-white rounded-2xl shadow-sm border border-lightGray p-6 mb-8">
        {/* Stats Row */}
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-lightGray">
          <span className="text-sm text-gray">
            Showing <strong className="text-primary">{data.items.length}</strong> of{' '}
            <strong className="text-secondary">{data.totalRecords}</strong> categories
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
          {dataLoading && data.items.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4"></div>
              <p className="text-gray">Loading categories...</p>
            </div>
          ) : data?.items?.length > 0 ? (
            data.items.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border border-lightGray p-4 shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-accent/40 transition-all duration-300 cursor-pointer flex items-center group"
                onClick={() => handleView(item.id)}
              >
                {/* Icon Box */}
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center mr-4 shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <FontAwesomeIcon icon={faLayerGroup} className="text-primary text-xl" />
                </div>

                {/* Content */}
                <div className="flex-grow min-w-0">
                  <h6 className="mb-1 text-base font-bold text-dark truncate group-hover:text-primary transition-colors">
                    {item.title}
                  </h6>
                  <div className="flex items-center text-xs text-gray space-x-3">
                    <span className="flex items-center">
                      <FontAwesomeIcon icon={faBook} className="mr-1.5" />
                      {item.books} Books
                    </span>
                    <span className="flex items-center truncate">
                      <FontAwesomeIcon icon={faCalendarAlt} className="mr-1.5" />
                      {new Date(item.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Action Arrow */}
                <div className="ml-3 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400">
                  <FontAwesomeIcon icon={faEye} />
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-16 bg-light rounded-xl border border-dashed border-gray-300">
              <FontAwesomeIcon
                icon={faLayerGroup}
                className="text-5xl text-gray-300 mb-4"
              />
              <p className="text-lg text-gray font-medium">No categories found</p>
              <p className="text-sm text-gray mt-1">Try adding a new category</p>
            </div>
          )}
        </div>

        {/* Load More Button */}
        {data?.pageNo < data?.totalPage && (
          <div className="text-center mt-8 pt-4">
            <button
              className="px-6 py-2.5 rounded-full bg-gradient-to-r from-primary to-orange-600 text-white font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
              disabled={dataLoading}
              onClick={() => getData(data.pageNo + 1, true)}
            >
              {dataLoading ? "Loading..." : "Load More Categories"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
